import { Form } from "antd/es";
import { Rule } from "antd/es/form";
import * as React from "react";
import KuikaSelectbox, { KuikaSelectboxOption } from "../../../ui-kit/kuika-selectbox/kuika-selectbox";
import KuikaSvg from "../../../ui-kit/kuika-svg/kuika-svg";
import { IConfigFormValues } from "./config-change-tracking";
import { ConfigField } from "./config-pane-parts";
import { ConfigFieldRevealContext, IConfigFieldReveal } from "./config-search-context";
import "./config-pane-parts.scss";

/**
 * "Pick a saved setting, or make a new one" — the row that appears ~60 times across the
 * configuration panes (mail, cache, session, maps, push, every MCP key, every auth
 * provider).
 *
 * `ConfigField` draws the row; this owns what only the caller knows: the form field, the
 * options, and what "new" and "edit" mean for its setting type.
 *
 * Each of those files rendered its own antd `Select` with the same three things hand-built
 * inside it: a chevron drawn as inline SVG with `stroke="black"`, an ADD NEW header made of
 * `Space` + `Typography.Link` tinted `#246EFF`, and a per-option edit glyph pasted as ~40
 * lines of path data filled `#246EFF`. All three are theme-blind, and antd's dropdown
 * portals to `document.body` — outside the themed pane — so the closed control could be
 * themed while its open menu stayed white.
 *
 * `KuikaSelectbox` draws its own chevron, owns its dropdown surface, and takes the ADD NEW
 * row as `dropdownHeader`. What a caller still owns is what only it knows: the form field,
 * the options, and what "new" and "edit" mean for its setting type.
 */

export interface IConfigSettingOption {
  id: string | null;
  name: string;
  /** False for a global setting shown inside a project configuration: it is read-only here. */
  editable?: boolean;
  /**
   * The tag `fetchConfigs` puts on a workspace setting while merging it into a project's
   * list. A project may pick one but not edit it, so it is read here rather than at each of
   * the ~15 call sites that pass these lists straight through — a caller cannot forget it.
   */
  isGlobalCommonConfigOnProjectConfig?: boolean;
}

export interface IConfigSettingSelectProps {
  /** The label in the row's fixed left column. "Configuration" almost everywhere. */
  label?: string;
  /** Draws the asterisk. Say it as well as putting a `required` rule in `rules`. */
  required?: boolean;
  /** The antd form path this value is bound to, e.g. `["apiConfig", "cacheSettingsId"]`. */
  formInputName: string[];
  options: IConfigSettingOption[];
  initialValue?: string | null;
  placeholder?: string;
  /** Key into `sd.configLocalizations` for the trailing (i). Omit for no tooltip. */
  tooltipContentKey?: string;
  addNewLabel?: string;
  onAddNew: () => void;
  /** Omit to make the options non-editable (a fixed list rather than saved settings). */
  onEdit?: (id: string) => void;
  allowClear?: boolean;
  disabled?: boolean;
  /**
   * Validation for the field, forwarded to the `Form.Item`. A few settings are required
   * conditionally — a Frontend URL once Kuika Cloud is on — which is also why
   * `dependencies` is here: antd re-runs the rules only for the fields it is told about.
   */
  rules?: Rule[];
  dependencies?: string[];
  /**
   * antd's value adapters, for the settings whose stored value is not the value the control
   * shows: Rate Limiting persists `null` for its built-in default but has to render a real
   * option, and the two that accept a cleared value normalize `""` back to `null`.
   */
  getValueProps?: (value: string | null) => object;
  normalize?: (value: string | null) => string | null;
  /**
   * The value inherited from the parent configuration. Present means this row OVERRIDES the
   * parent, which is what the tinted control and the restore affordance say.
   */
  parentConfigValue?: string | null;
  onRestore?: () => void;
  /** Renders the row in the pane's narrow right column: label above its control. */
  aside?: boolean;
  /**
   * The field names inside the dialog this row's ADD NEW / edit opens, for the search. See
   * `IConfigFieldProps.searchTerms`: a dialog is mounted only while it is open, so its
   * contents cannot be collected the way a label is.
   */
  searchTerms?: string[];
  dropdownTestId?: string;
  addNewTestId?: string;
  editTestId?: string;
}

interface IConfigSettingDialogOpenerProps {
  /** The setting currently chosen, or null for "Default" — the absence of a saved setting. */
  value: string | null;
  onAddNew: () => void;
  onEdit?: (id: string) => void;
}

/**
 * Opens the row's dialog when a search result asked for something only the dialog holds.
 *
 * Which dialog to open depends on the value: the chosen setting if there is one, a new one
 * if the row is still on Default. So it is rendered under a `shouldUpdate` render prop —
 * antd 4.16 has no `Form.useWatch` (the same reason `kuika-parameters-table` uses that
 * pattern) — and draws nothing itself.
 */
const ConfigSettingDialogOpener = ({ value, onAddNew, onEdit }: IConfigSettingDialogOpenerProps) => {
  const reveal: IConfigFieldReveal = React.useContext(ConfigFieldRevealContext);
  // Read through a ref so the effect fires on the REQUEST, not every time the value changes.
  const latest: React.MutableRefObject<IConfigSettingDialogOpenerProps> = React.useRef({ value, onAddNew, onEdit });
  latest.current = { value, onAddNew, onEdit };

  React.useEffect((): void => {
    if (!reveal.wantsDialog) return;
    const { value: current, onAddNew: addNew, onEdit: edit } = latest.current;
    if (current && edit) {
      edit(current);
      return;
    }
    addNew();
  }, [reveal.wantsDialog, reveal.token]);

  return null;
};

export class ConfigSettingSelect extends React.PureComponent<IConfigSettingSelectProps> {
  /**
   * The edit glyph sits inside the option, so its click must not also select that option.
   * rc-select commits on mousedown, which is why that is the event being stopped.
   */
  handleEdit = (e: React.MouseEvent, id: string): void => {
    e.preventDefault();
    e.stopPropagation();
    this.props.onEdit?.(id);
  };

  /**
   * This row's value out of a whole form snapshot.
   *
   * antd re-runs every `shouldUpdate` on every store update — any field, anywhere in the one
   * `Form` that spans the editor — and about sixty of these rows are mounted. So this reads
   * the one key and allocates nothing: it used to build `Object.entries` and a `Map` over
   * the whole slice to fetch a single property, twice per comparison, which turned a
   * keystroke in any field into a hundred-odd throwaway arrays.
   */
  readValue = (values: IConfigFormValues): unknown => {
    const [slice, key] = this.props.formInputName;
    return values[slice]?.[key];
  };

  /** Stable so antd is not handed a new comparator on every render. */
  hasValueChanged = (previous: IConfigFormValues, next: IConfigFormValues): boolean =>
    this.readValue(previous) !== this.readValue(next);

  /** A workspace setting seen from a project is selectable but not editable. */
  isEditable = (option: IConfigSettingOption): boolean =>
    option.editable !== false && option.isGlobalCommonConfigOnProjectConfig !== true;

  getOptions = (): KuikaSelectboxOption[] =>
    (this.props.options ?? []).map((option: IConfigSettingOption): KuikaSelectboxOption => ({
      // The "Default" row carries a null id, which is what this control writes back when it is
      // picked — an empty string would read as a change to the dirty check. The kit takes null
      // now (it swaps in its own sentinel for rc-select), so the cast is gone with it.
      value: option.id ?? null,
      searchLabel: option.name,
      label: (
        <span className="kuika-config-setting__option">
          <span className="kuika-config-setting__option-name">{option.name}</span>
          {option.id && this.isEditable(option) && this.props.onEdit && (
            <button
              type="button"
              className="kuika-config-setting__option-edit"
              aria-label={`Edit ${option.name}`}
              data-testid={this.props.editTestId}
              onMouseDown={(e: React.MouseEvent) => this.handleEdit(e, option.id as string)}
            >
              <KuikaSvg name="settings" width={16} height={16} />
            </button>
          )}
        </span>
      )
    }));

  /**
   * "ADD NEW · 3 saved" — the line under the control.
   *
   * Creating a setting used to be a row inside the open dropdown, which meant the only way
   * to learn that a configuration HAS saved settings, or how many, was to open it. The
   * count is the useful half: a row reading "Default · 0 saved" says at a glance that
   * nothing has been set up for it yet.
   *
   * The "Default" entry is not a saved setting — it is the absence of one — so it does not
   * count.
   */
  renderHint = (): React.ReactNode => {
    const saved: number = (this.props.options ?? []).filter(
      (option: IConfigSettingOption): boolean => option.id !== null && option.id !== undefined
    ).length;

    return (
      <>
        <button
          type="button"
          className="kuika-config-setting__add-new"
          data-testid={this.props.addNewTestId}
          onClick={this.props.onAddNew}
        >
          {this.props.addNewLabel ?? "ADD NEW"}
        </button>
        <span className="kuika-config-setting__saved-count">
          {" · "}
          {saved} saved
        </span>
      </>
    );
  };

  render() {
    const isOverridden: boolean = this.props.parentConfigValue !== undefined;

    return (
      <ConfigField
        formInputName={this.props.formInputName}
        searchTerms={this.props.searchTerms}
        // This row renders the opener below, so a hit on those terms can be answered.
        searchTermsOpenDialog
        label={this.props.label ?? "Configuration"}
        required={this.props.required}
        aside={this.props.aside}
        tooltipContentKey={this.props.tooltipContentKey}
        onRestore={isOverridden ? this.props.onRestore : undefined}
        hint={this.renderHint()}
      >
        <Form.Item
          name={this.props.formInputName}
          initialValue={this.props.initialValue ?? null}
          rules={this.props.rules}
          dependencies={this.props.dependencies}
          getValueProps={this.props.getValueProps}
          normalize={this.props.normalize}
        >
          <KuikaSelectbox
            size="md"
            options={this.getOptions()}
            placeholder={this.props.placeholder ?? "Choose Configuration"}
            allowClear={this.props.allowClear}
            disabled={this.props.disabled}
            className={isOverridden ? "overridden" : undefined}
            data-testid={this.props.dropdownTestId}
          />
        </Form.Item>
        {/* Only for a row whose dialog a search can point INTO. `ConfigField` reads the
            path off `formInputName`, so this second `Form.Item` — a render prop with no
            name of its own — is invisible to the form's values. */}
        {this.props.searchTerms !== undefined && (
          <Form.Item noStyle shouldUpdate={this.hasValueChanged}>
            {(form) => (
              <ConfigSettingDialogOpener
                value={form.getFieldValue(this.props.formInputName) ?? null}
                onAddNew={this.props.onAddNew}
                onEdit={this.props.onEdit}
              />
            )}
          </Form.Item>
        )}
      </ConfigField>
    );
  }
}
